import pkg from '../../package.json';
import { configAnalytics, getConfig, setConfig } from '../configs';
import analytics from '../connectors/analytics';
import AnalyticsWrapper from '../connectors/analyticsWrapper';
import messages from '../constants/logMessages.json';
import { shouldShowQalBanner } from '../constants/qalEnvironments';
import * as events from '../tracking/event';
import tracking from '../tracking/tracking';
import { AnalyticsConst, AnalyticsEventTypes } from '../types';
import logging from '../utils/logging';
import analyticsEventBus from '../utils/mfeEventBus/analyticsEventBus';
import tools from '../utils/tools/index';

/** Options for explicitly initialising the QAL runtime. */
export type InitQalOptions = {
  /**
   * Environment passed by the consumer (recommended).
   * e.g. initQAL({ env: import.meta.env.MODE }) in Vite apps
   */
  env?: string;

  /**
   * If true, prints the QAL banner to console.
   * Default: true in local and dev/development; false otherwise.
   */
  showQalBanner?: boolean;

  /**
   * If true, attaches a QAL API object to window.QAL (for debugging / legacy usage).
   * Default: false
   */
  exposeQalGlobal?: boolean;

  /**
   * For tests or special cases: allow re-init (otherwise initQAL is idempotent).
   * Default: false
   */
  force?: boolean;
};

let initialized = false;

function getDefaultEnv(): string {
  // Safe in Node/Jest. In Vite/browser, process may be undefined, so this falls back to 'development'.
  // Consumers should pass env explicitly for best accuracy.
  // eslint-disable-next-line no-undef
  if (typeof process !== 'undefined' && process.env) {
    // eslint-disable-next-line no-undef
    return process.env.MODE || process.env.NODE_ENV || 'development';
  }
  return 'development';
}

function printBanner() {
  const { logo, infoRegular } = messages;

  logging.info(
    `%c
    ██████╗  █████╗  ██╗     
    ██╔═══██╗██╔══██╗██║     
    ██║   ██║███████║██║     
    ██║▄▄ ██║██╔══██║██║     
    ╚██████╔╝██║  ██║███████╗
      ╚═▀▀═╝ ╚═╝  ╚═╝╚══════╝
    %c QBE Analytics Library %c
     
    QAL: v${pkg.version}
    Documentation: https://qbe-appservices.atlassian.net/wiki/spaces/WA/pages/5110792239/QAL+Installation
    `,
    'color: #00aff4',
    logo,
    infoRegular
  );
}

function attachGlobal() {
  if (typeof window === 'undefined') return;

  (window as any).QAL = {
    analytics,
    AnalyticsConst,
    AnalyticsEventTypes,
    AnalyticsWrapper,
    configAnalytics,
    events,
    getConfig,
    logging,
    setConfig,
    tools,
    tracking,
    analyticsEventBus,
  };
}

/**
 * Explicitly initialises the QAL runtime.
 *
 * Call this once in SPA bootstrap when you need logging/banner/global setup without calling
 * `configAnalytics`. Normal applications can call `configAnalytics`, which invokes `initQAL`
 * automatically. The initializer is idempotent unless `force` is true.
 *
 * Side effects include setting QAL log level, optionally printing the QAL banner, and optionally
 * exposing `window.QAL` for debugging and legacy consumers.
 *
 * @param options - Runtime environment, banner/global exposure options, and optional force re-init.
 * @returns Nothing.
 *
 * @example
 * initQAL({
 *   env: import.meta.env.MODE,
 *   exposeQalGlobal: false,
 * });
 */
export function initQAL(options: InitQalOptions = {}) {
  const {
    env: envOverride,
    showQalBanner,
    exposeQalGlobal = true,
    force = false,
  } = options;

  if (initialized && !force) return;

  const env = (envOverride || getDefaultEnv()).toLowerCase();
  logging.setLogLevel(env);

  const shouldShowBanner =
    typeof showQalBanner === 'boolean'
      ? showQalBanner
      : shouldShowQalBanner(env);

  if (shouldShowBanner) {
    printBanner();
  }

  if (exposeQalGlobal) {
    attachGlobal();
  }

  initialized = true;
}

/**
 * Resets QAL initialisation state for tests.
 *
 * Use in test teardown when a suite needs to verify repeated `initQAL` or `configAnalytics`
 * behaviour. This does not clear analytics config, data layer contents, or milestone state.
 *
 * @returns Nothing.
 */
export function resetQALInitForTests() {
  initialized = false;
}
